import { CommonModule } from '@angular/common';
import { Component, DestroyRef, EventEmitter, Inject, inject, OnInit, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AiKnowledgeI18nModule } from '../../assets/i18n/ai-knowledge-i18n.module';
import { MatCardModule } from '@angular/material/card';
import { CustomDataComponent } from '../custom-data/custom-data.component';
import { WebsiteScrapeComponent } from '../website-scrape/website-scrape.component';
import { FileUploadComponent } from '../file-upload/file-upload.component';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { FeatureFlagService } from '@galaxy/partner';
import { map, catchError, take } from 'rxjs/operators';
import { of } from 'rxjs';

import { MatButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogConfig,
  MatDialogContent,
  MatDialogRef,
} from '@angular/material/dialog';

import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ComponentType } from '@angular/cdk/portal';
import { KnowledgeCreatedEvent } from '../common/interfaces';
import { KnowledgeSourceConfig, KnowledgeSourceKey } from '@vendasta/embeddings';
import { PARTNER_ID_TOKEN } from '../../tokens';
import { toSignal } from '@angular/core/rxjs-interop';
import { distinctUntilChanged } from 'rxjs/operators';

const FILE_UPLOAD_FEATURE_FLAG = 'ai_knowledge_file_upload';

interface Choice {
  title: string;
  description: string;
  component: any;
}

interface Data {
  knowledgeSourceKey?: KnowledgeSourceKey;
  config?: KnowledgeSourceConfig;
  isSuggested?: boolean;
  stepOption?: string;
  excludeAddToAIStep?: boolean;
}

@Component({
  selector: 'ai-knowledge-add-knowledge',
  imports: [
    CommonModule,
    TranslateModule,
    AiKnowledgeI18nModule,
    MatCardModule,
    GalaxyLoadingSpinnerModule,
    MatButton,
    MatDialogActions,
    MatDialogContent,
  ],
  providers: [],
  templateUrl: './add-knowledge.component.html',
  styleUrls: ['./add-knowledge.component.scss'],
})
export class AddKnowledgeComponent implements OnInit {
  private readonly DIALOG_WIDTH = '660px';
  private readonly bpObserver = inject(BreakpointObserver);
  private readonly dialog = inject(MatDialog);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly partnerId$ = inject(PARTNER_ID_TOKEN).pipe(distinctUntilChanged());
  private readonly partnerId = toSignal(this.partnerId$);

  readonly availableChoices = signal<Choice[]>([]);
  componentSelected = new EventEmitter<{ knowledgeCreated: EventEmitter<string>; destroyRef: DestroyRef }>();

  private readonly allChoices: Choice[] = [
    {
      title: 'EDIT_KNOWLEDGE.CUSTOM_DATA_CONFIG.TITLE',
      description: 'EDIT_KNOWLEDGE.CUSTOM_DATA_CONFIG.DESCRIPTION',
      component: CustomDataComponent,
    },
    {
      title: 'EDIT_KNOWLEDGE.WEBSITE_SCRAPE_CONFIG.TITLE',
      description: 'EDIT_KNOWLEDGE.WEBSITE_SCRAPE_CONFIG.DESCRIPTION',
      component: WebsiteScrapeComponent,
    },
    {
      title: 'EDIT_KNOWLEDGE.FILE_UPLOAD_CONFIG.TITLE',
      description: 'EDIT_KNOWLEDGE.FILE_UPLOAD_CONFIG.DESCRIPTION',
      component: FileUploadComponent,
    },
  ];

  constructor(
    public dialogRef: MatDialogRef<AddKnowledgeComponent>,
    public readonly destroyRef: DestroyRef,
    @Inject(MAT_DIALOG_DATA) public data: Data,
  ) {}

  ngOnInit(): void {
    this.dialogRef.disableClose = true;

    // Check feature flag and filter choices
    const partnerId = this.partnerId();
    if (partnerId) {
      this.featureFlagService
        .batchGetStatus(partnerId, '', [FILE_UPLOAD_FEATURE_FLAG])
        .pipe(
          map((resp) => resp[FILE_UPLOAD_FEATURE_FLAG]),
          catchError(() => of(false)),
          take(1),
        )
        .subscribe((fileUploadEnabled) => {
          if (fileUploadEnabled) {
            this.availableChoices.set(this.allChoices);
          } else {
            // Filter out file upload option
            this.availableChoices.set(this.allChoices.filter((choice) => choice.component !== FileUploadComponent));
          }
        });
    } else {
      // Default to showing all choices if no partner ID
      this.availableChoices.set(this.allChoices);
    }
  }

  selectComponent(choice: Choice): void {
    const component = this.openDialog(choice.component);
    this.componentSelected.emit({ knowledgeCreated: component.knowledgeCreated, destroyRef: component.destroyRef });
    this.close();
  }

  private openDialog(type: ComponentType<KnowledgeCreatedEvent>): KnowledgeCreatedEvent {
    const conf: MatDialogConfig = {
      width: this.DIALOG_WIDTH,
      data: { excludeAddToAIStep: this.data?.excludeAddToAIStep },
    };

    const isSmallScreen = this.bpObserver.isMatched(Breakpoints.XSmall);
    if (isSmallScreen) {
      conf.maxWidth = '100vw';
      conf.maxHeight = '100vh';
    }
    const editor = this.dialog.open(type, conf);
    return editor.componentInstance;
  }

  close(): void {
    this.dialogRef.close();
  }
}
